import { useState } from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

// Alert variant configurations
const alertVariants = {
  success: {
    containerClass: "bg-green-50 border-green-200",
    iconClass: "text-green-400",
    titleClass: "text-green-800",
    descriptionClass: "text-green-700",
    icon: CheckCircleIcon,
  },
  warning: {
    containerClass: "bg-yellow-50 border-yellow-200",
    iconClass: "text-yellow-400",
    titleClass: "text-yellow-800",
    descriptionClass: "text-yellow-700",
    icon: ExclamationTriangleIcon,
  },
  error: {
    containerClass: "bg-red-50 border-red-200",
    iconClass: "text-red-400",
    titleClass: "text-red-800",
    descriptionClass: "text-red-700",
    icon: XCircleIcon,
  },
  info: {
    containerClass: "bg-blue-50 border-blue-200",
    iconClass: "text-blue-400",
    titleClass: "text-blue-800",
    descriptionClass: "text-blue-700",
    icon: InformationCircleIcon,
  },
  default: {
    containerClass: "bg-gray-50 border-gray-200",
    iconClass: "text-gray-400",
    titleClass: "text-gray-800",
    descriptionClass: "text-gray-700",
    icon: InformationCircleIcon,
  },
};

export default function Alert({
  variant = "default",
  title,
  description,
  children,
  isClosable = false,
  isCollapsible = false,
  defaultCollapsed = false,
  onClose,
  className = "",
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const config = alertVariants[variant] || alertVariants.default;
  const IconComponent = config.icon;

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`rounded-md border p-4 ${config.containerClass} ${className}`}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <IconComponent
            className={`h-5 w-5 ${config.iconClass}`}
            aria-hidden="true"
          />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {title && (
                <h3
                  className={`text-sm font-medium ${config.titleClass} ${
                    isCollapsible ? "cursor-pointer" : ""
                  }`}
                  onClick={isCollapsible ? toggleCollapsed : undefined}
                >
                  <div className="flex items-center">
                    {title}
                    {isCollapsible && (
                      <span className="ml-2">
                        {isCollapsed ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronUpIcon className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </div>
                </h3>
              )}
              
              {/* Content area - collapsible */}
              {(!isCollapsible || !isCollapsed) && (
                <div className={title ? "mt-2" : ""}>
                  {description && (
                    <div className={`text-sm ${config.descriptionClass}`}>
                      <p>{description}</p>
                    </div>
                  )}
                  {children && (
                    <div className={`text-sm ${config.descriptionClass}`}>
                      {children}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Close button */}
            {isClosable && (
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    onClick={handleClose}
                    className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:bg-opacity-20 hover:bg-gray-600 ${config.iconClass} focus:ring-gray-600`}
                  >
                    <span className="sr-only">Dismiss</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Convenience components for specific variants
export const SuccessAlert = (props) => <Alert variant="success" {...props} />;
export const WarningAlert = (props) => <Alert variant="warning" {...props} />;
export const ErrorAlert = (props) => <Alert variant="error" {...props} />;
export const InfoAlert = (props) => <Alert variant="info" {...props} />; 